import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  OnDestroy,
  forwardRef,
  Injector,
} from '@angular/core';
import { AbstractMethodComponent } from '../abstract-method.component';
import { OrderformFascade } from '../../../../fascades/orderform.fascade';
import { PaymentMethodsComponent } from '../../payment-methods.component';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

declare let braintree: any;

export interface BraintreeLib {
  venmo: {
    create(
      options: any,
      callback: (err: any, instance: BraintreeVenmo) => void
    );
  };
  client: {
    create(options: any, callback: (err, instance: any) => void);
  };
}

export interface BraintreeVenmo {
  tokenize(
    callback: (error: any, payload: { nonce: any; deviceData: string }) => void
  );
  isBrowserSupported(): boolean;
}

@Component({
  selector: 'orderform-payment-method-venmo',
  templateUrl: './venmo.component.html',
  styleUrls: ['./venmo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VenmoComponent),
      multi: true,
    },
  ],
})
export class VenmoComponent
  extends AbstractMethodComponent
  implements OnInit, OnDestroy
{
  @Input() config: {
    jsSdkUrls: string[];
    clientToken: string;
    locale: string;
    has3dSecure: boolean;
    debugLabel: string;
    config: any;
  };

  private _subscriptionBeforeSubmit =
    this._orderformFascade.beforeSubmit.subscribe((lastResult) =>
      this._onBeforeSubmit(lastResult)
    );

  private _waitForLoad: Promise<void>;
  private _venmoInstance: BraintreeVenmo;

  constructor(
    private _orderformFascade: OrderformFascade,
    private _paymentMethodComponent: PaymentMethodsComponent,
    injector: Injector
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this._waitForLoad = this._load();
    this._debugLabel = this.config.debugLabel;
  }

  private async _load(): Promise<void> {
    await this._loadExternalResources(this.config.jsSdkUrls);
    braintree.client.create(
      {
        authorization: this.config.clientToken,
        container: '#venmo-container-' + this.uniqueInstanceId,
        locale: this.config.locale,
      },
      (clientErr, clientInstance) => {
        if (clientErr) {
          this._hidePaymentMethodWithError(clientErr);
          return;
        }
        braintree.venmo.create(
          {
            ...this.config.config,
            client: clientInstance,
          },
          (venmoErr, venmoInstance) => {
            if (venmoErr) {
              this._hidePaymentMethodWithError(venmoErr);
              return;
            }
            if (!venmoInstance.isBrowserSupported()) {
              this._hidePaymentMethodWithError(
                'Venmo does not support your browser.'
              );
              return;
            }

            this._venmoInstance = venmoInstance;
          }
        );
      }
    );
  }

  private async _onBeforeSubmit(
    resultListBefore: { valid: boolean }[]
  ): Promise<{ valid: boolean }> {
    await this._waitForLoad;

    if (
      !resultListBefore.reduce((valid, result) => valid && result.valid, true)
    ) {
      return { valid: false };
    }

    return new Promise((resolve, reject) =>
      this._venmoInstance.tokenize((error: any, payload) => {
        if (!error && payload && typeof payload.nonce != 'undefined') {
          console.log(
            'DS24-PP ' + this._debugLabel + ': payment authorized',
            payload
          );
          const device_data =
            typeof payload.deviceData === 'string'
              ? payload.deviceData.replace(/\"/g, '::Q::')
              : '';
          this._paymentDataChanged.next({
            valid: true,
            braintree_nonce: payload.nonce,
            braintree_device: device_data,
          });
          resolve({ valid: true });
        } else {
          console.log(
            'DS24-PP ' + this._debugLabel + ': payment error: ',
            error
          );
          const errorElement = document.querySelector(
            '.braintree-sheet__error-text'
          );
          const isErrorElementVisible =
            errorElement && errorElement.getBoundingClientRect().height > 0;
          if (!isErrorElementVisible) {
            this._toaster.error(error.message);
          }

          this._monitoring.trackValidationError(
            'exception:' + error.message,
            'frontend',
            'ds-orderform-payment-venmo'
          );
          resolve({ valid: false });
        }
      })
    );
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._subscriptionBeforeSubmit.unsubscribe();
  }
}
